import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/environment';
import { toast } from 'sonner';
import BackButton from '../components/BackButton';
import BookingRescheduleDialog from '../components/BookingRescheduleDialog';
import RefundRulesCard from '../components/RefundRulesCard';
import { formatDateFR, formatTimeFR, formatTimeRangeFR, formatDateLongFR, getStatusLabel, getStatusStyle, toLocalDateStr } from '../utils/formatFR';
import StatusBadge from '../components/StatusBadge';
import CoachEnterpriseOffers from '../components/coach/CoachEnterpriseOffers';
import CoachPayoutsTab from '../components/coach/CoachPayoutsTab';
import CoachBookingSharedContext from '../components/coach/CoachBookingSharedContext';
import {
  User, Calendar, BarChart3, Settings, Plus, Trash2,
  Clock, Euro, Check, X, ExternalLink, Download, FileText,
  ArrowLeft, ChevronLeft, ChevronRight, Video, Save, CalendarRange,
  Camera, CreditCard, Award, Upload, Loader2, Tag,
  AlertTriangle, CalendarClock, Crown, Sparkles, Percent, Building2
} from 'lucide-react';

const API = getApiUrl();

const PLAN_TIER_STYLES = {
  starter: { label: 'Starter', Icon: Tag,      bg: 'bg-zinc-100 text-zinc-700 border-zinc-300',           accent: '#71717A' },
  pro:     { label: 'Pro',     Icon: Sparkles, bg: 'bg-[#FEF9E7] text-[#92710A] border-[#F5E6A3]',         accent: '#D4AF37' },
  elite:   { label: 'Elite',   Icon: Crown,    bg: 'bg-purple-50 text-purple-700 border-purple-200',       accent: '#9333EA' },
};

function CoachPlanBadge({ planId, size = 'md' }) {
  const pid = (planId || '').toLowerCase();
  const style = PLAN_TIER_STYLES[pid];
  if (!style) return null;
  const { Icon, label, bg } = style;
  const sizeCls = size === 'lg' ? 'px-3 py-1 text-xs' : 'px-2 py-0.5 text-[11px]';
  return (
    <span
      data-testid={`coach-plan-badge-${pid}`}
      className={`inline-flex items-center gap-1 rounded-full border ${bg} ${sizeCls} font-medium`}
    >
      <Icon className={size === 'lg' ? 'w-3.5 h-3.5' : 'w-3 h-3'} /> {label}
    </span>
  );
}

const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

const SPECIALTIES_SUGGESTIONS = [
  'Leadership', 'Management', 'Communication', 'Gestion du stress',
  'Prise de parole', 'Intelligence émotionnelle', 'Gestion de conflits',
  'Transition de carrière', 'Performance', 'Organisation',
  'Coaching d\'équipe', 'Développement personnel',
];


const SPECIALTIES_OPTIONS = [
  'Leadership', 'Management', 'Communication', 'Gestion du stress',
  'Prise de parole', 'Intelligence émotionnelle', 'Gestion de conflits',
  'Transition de carrière', 'Performance', 'Organisation',
  'Coaching d\'équipe', 'Développement personnel',
];

// Composant select heure format FR 24h (06h00 → 22h00, pas de 30 min)
const HOURS = Array.from({ length: 17 }, (_, i) => i + 6); // 6..22
const MINUTES = [0, 30];

function formatHM(h, m) {
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
function displayHM(h, m) {
  return `${String(h).padStart(2, '0')}h${String(m).padStart(2, '0')}`;
}

function TimePickerWheel({ value, onChange, label }) {
  const [open, setOpen] = useState(false);
  const parsed = (value || '09:00').split(':');
  const [selH, setSelH] = useState(parseInt(parsed[0]) || 9);
  const [selM, setSelM] = useState(parseInt(parsed[1]) || 0);
  const [step, setStep] = useState('hour');

  useEffect(() => {
    const p = (value || '09:00').split(':');
    setSelH(parseInt(p[0]) || 9);
    setSelM(parseInt(p[1]) || 0);
  }, [value]);

  const cancel = () => { setOpen(false); setStep('hour'); };
  const selectHour = (h) => { setSelH(h); setStep('minute'); };
  const selectMinute = (m) => {
    setSelM(m);
    onChange(formatHM(selH, m));
    setOpen(false);
    setStep('hour');
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={`time-picker-${label}`}
        className="w-full px-3 py-2.5 bg-[#F8F7F4] border border-[#DDD9D0] rounded-lg text-[#111827] text-sm text-left flex items-center justify-between hover:border-[#D4AF37] transition-colors">
        <span className="font-medium">{displayHM(selH, selM)}</span>
        <Clock className="w-3.5 h-3.5 text-[#92710A]" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={cancel}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative w-full max-w-xs bg-white rounded-2xl shadow-2xl mx-4 p-5"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <button onClick={cancel} className="text-[#4B5563] text-sm font-medium">Annuler</button>
              <span className="text-[#111827] text-base font-bold">{displayHM(selH, selM)}</span>
              {step === 'minute' ? (
                <button onClick={() => setStep('hour')} className="text-[#92710A] text-sm font-medium">Retour</button>
              ) : <div className="w-12" />}
            </div>

            {step === 'hour' ? (
              <>
                <p className="text-xs text-[#4B5563] text-center mb-3 font-medium">Selectionnez l'heure</p>
                <div className="grid grid-cols-4 gap-1.5">
                  {HOURS.map(h => (
                    <button key={h} type="button" onClick={() => selectHour(h)}
                      className={`py-2.5 rounded-lg text-sm font-semibold transition-all ${
                        selH === h
                          ? 'bg-[#FEF9E7] text-[#92710A] border-2 border-[#D4AF37] shadow-sm'
                          : 'bg-[#F8F7F4] text-[#111827] border border-[#E8E5DE] hover:border-[#D4AF37] hover:bg-[#FEF9E7]'
                      }`}>
                      {String(h).padStart(2, '0')}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <p className="text-xs text-[#4B5563] text-center mb-3 font-medium">Selectionnez les minutes</p>
                <div className="grid grid-cols-2 gap-3">
                  {MINUTES.map(m => (
                    <button key={m} type="button" onClick={() => selectMinute(m)}
                      className={`py-4 rounded-xl text-lg font-bold transition-all ${
                        selM === m
                          ? 'bg-[#FEF9E7] text-[#92710A] border-2 border-[#D4AF37] shadow-sm'
                          : 'bg-[#F8F7F4] text-[#111827] border border-[#E8E5DE] hover:border-[#D4AF37] hover:bg-[#FEF9E7]'
                      }`}>
                      {String(selH).padStart(2, '0')}h{String(m).padStart(2, '0')}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function DocumentUploader({ token, profile, onUploaded }) {
  const API = getApiUrl();
  const [uploading, setUploading] = React.useState({});
  const [docs, setDocs] = React.useState({
    profile_photo: !!profile.profile_photo_path,
    id_document: !!profile.id_document_path,
    coach_certificate: !!profile.coach_certificate_path,
    business_proof: !!profile.business_proof_path,
  });

  const handleUpload = async (docType, file) => {
    if (!file) return;
    setUploading(prev => ({ ...prev, [docType]: true }));
    try {
      const res = await fetch(`${API}/coach/documents/upload?doc_type=${docType}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': file.type || 'application/octet-stream', 'X-Filename': file.name },
        body: file,
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Erreur'); }
      setDocs(prev => ({ ...prev, [docType]: true }));
      const LABELS = {
        profile_photo: 'Photo',
        id_document: "Pièce d'identité",
        coach_certificate: 'Certificat',
        business_proof: "Justificatif d'exercice professionnel",
      };
      toast.success(`${LABELS[docType] || docType} uploadé(e)`);
      if (onUploaded) onUploaded();
    } catch (err) { toast.error(err.message); }
    setUploading(prev => ({ ...prev, [docType]: false }));
  };

  const DOC_ITEMS = [
    { key: 'profile_photo', label: 'Photo de profil', icon: Camera, accept: 'image/*', desc: 'Photo professionnelle (JPG, PNG)' },
    { key: 'id_document', label: 'Pièce d\'identité', icon: CreditCard, accept: 'image/*,application/pdf', desc: 'CNI, passeport (JPG, PNG, PDF)' },
    { key: 'coach_certificate', label: 'Certificat coach', icon: Award, accept: 'image/*,application/pdf', desc: 'Diplôme ou certification (JPG, PNG, PDF)' },
    { key: 'business_proof', label: 'Justificatif d\'exercice professionnel', icon: Building2, accept: 'image/*,application/pdf', desc: 'Kbis, SIRENE, SIRET, URSSAF ou équivalent (JPG, PNG, PDF)' },
  ];

  return (
    <div>
      <label className="text-sm text-[#4B5563] mb-2 block font-medium">Justificatifs & photo</label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {DOC_ITEMS.map(({ key, label, icon: Icon, accept, desc }) => (
          <div key={key} className={`bg-[#F8F7F4] border rounded-xl p-3.5 transition-all ${docs[key] ? 'border-green-300' : 'border-[#DDD9D0]'}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${docs[key] ? 'bg-green-100' : 'bg-[#F5F0E3]'}`}>
                {docs[key] ? <Check className="w-4 h-4 text-green-600" /> : <Icon className="w-4 h-4 text-[#92710A]" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[#111827] text-xs font-medium truncate">{label}</p>
                <p className="text-[#4B5563] text-[10px]">{docs[key] ? 'Déposé' : desc}</p>
              </div>
            </div>
            <label className={`w-full py-2 rounded-lg text-xs font-medium cursor-pointer transition-all flex items-center justify-center gap-1.5 ${
              docs[key] ? 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200' : 'bg-white text-[#92710A] hover:bg-[#F5F0E3] border border-[#E8DFC8]'
            } ${uploading[key] ? 'opacity-50 pointer-events-none' : ''}`}>
              {uploading[key] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
              {uploading[key] ? 'Upload...' : docs[key] ? 'Remplacer' : 'Choisir'}
              <input type="file" accept={accept} className="hidden" data-testid={`upload-${key}`}
                onChange={(e) => { if (e.target.files[0]) handleUpload(key, e.target.files[0]); }} />
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}


function ProfileEditor({ profile, token, onSaved }) {
  const [form, setForm] = React.useState({
    display_name: profile.display_name || '',
    bio: profile.bio || '',
    specialties: profile.specialties || [],
    hourly_rate: profile.hourly_rate || 80,
    session_duration: profile.session_duration || 60,
    phone: profile.phone || '',
    linkedin_url: profile.linkedin_url || '',
    certifications: profile.certifications || [],
  });
  const [newSpec, setNewSpec] = React.useState('');
  const [newCert, setNewCert] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const API = getApiUrl();

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        hourly_rate: form.hourly_rate === '' ? 0 : Number(form.hourly_rate),
        session_duration: form.session_duration === '' ? 60 : Number(form.session_duration),
      };
      const res = await fetch(`${API}/coach/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Erreur');
      toast.success('Profil mis à jour');
      onSaved();
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  return (
    <div className="pb-20 lg:pb-0">
      <div className="bg-white border border-[#E8E5DE] rounded-xl shadow-sm p-4 sm:p-6 space-y-5">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-[#111827] font-medium">Modifier le profil</h3>
        <a href={`/marketplace/coach/${profile.user_id}`} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-[#4B5563] hover:text-[#111827] text-xs transition-colors">
          <ExternalLink className="w-3.5 h-3.5" /> Voir public
        </a>
      </div>

      <div>
        <label className="text-sm text-[#4B5563] mb-1.5 block">Nom affiché</label>
        <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })}
          className="bg-[#F8F7F4] border-[#DDD9D0] text-[#111827] rounded-xl" data-testid="edit-display-name" />
      </div>

      <div>
        <label className="text-sm text-[#4B5563] mb-1.5 block">Bio</label>
        <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })}
          className="bg-[#F8F7F4] border-[#DDD9D0] text-[#111827] rounded-xl min-h-[100px]" data-testid="edit-bio" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm text-[#4B5563] mb-1.5 block">Tarif horaire (€)</label>
          <Input type="number" value={form.hourly_rate === '' ? '' : form.hourly_rate} data-testid="edit-hourly-rate"
            onChange={(e) => setForm({ ...form, hourly_rate: e.target.value === '' ? '' : parseFloat(e.target.value) })}
            className="bg-[#F8F7F4] border-[#DDD9D0] text-[#111827] rounded-xl" min="0" step="0.5" placeholder="Ex: 80" />
        </div>
        <div>
          <label className="text-sm text-[#4B5563] mb-1.5 block">Durée session (min)</label>
          <Input type="number" value={form.session_duration === '' ? '' : form.session_duration} data-testid="edit-duration"
            onChange={(e) => setForm({ ...form, session_duration: e.target.value === '' ? '' : parseInt(e.target.value) })}
            className="bg-[#F8F7F4] border-[#DDD9D0] text-[#111827] rounded-xl" min="15" step="15" placeholder="Ex: 60" />
        </div>
      </div>

      <div>
        <label className="text-sm text-[#4B5563] mb-1.5 block">Spécialités sélectionnées : {form.specialties.length}</label>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {form.specialties.map((s, i) => (
            <Badge key={i} className="bg-[#D4AF37]/15 text-[#92710A] border-[#D4AF37]/30 cursor-pointer hover:bg-red-500/20 hover:text-red-400"
              onClick={() => setForm({ ...form, specialties: form.specialties.filter((_, j) => j !== i) })}>
              {s} <X className="w-3 h-3 ml-1" />
            </Badge>
          ))}
        </div>
        <div className="flex gap-2 mb-2">
          <Input value={newSpec} onChange={(e) => setNewSpec(e.target.value)} placeholder="Ajouter..."
            className="bg-[#F8F7F4] border-[#DDD9D0] text-[#111827] rounded-xl"
            onKeyDown={(e) => { if (e.key === 'Enter' && newSpec.trim()) { e.preventDefault(); setForm({ ...form, specialties: [...form.specialties, newSpec.trim()] }); setNewSpec(''); }}} />
        </div>
        <div className="flex flex-wrap gap-1">
          {SPECIALTIES_OPTIONS.filter(s => !form.specialties.includes(s)).slice(0, 6).map(s => (
            <button key={s} onClick={() => setForm({ ...form, specialties: [...form.specialties, s] })}
              className="px-2 py-1 rounded text-xs bg-white/5 text-[#4B5563] hover:text-[#111827] hover:bg-white/10 transition-all">+ {s}</button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-sm text-[#4B5563] mb-1.5 block">Certifications</label>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {form.certifications.map((c, i) => (
            <Badge key={i} variant="outline" className="bg-white/5 text-[#4B5563] border-[#DDD9D0] cursor-pointer hover:bg-red-500/20"
              onClick={() => setForm({ ...form, certifications: form.certifications.filter((_, j) => j !== i) })}>
              {c} <X className="w-3 h-3 ml-1" />
            </Badge>
          ))}
        </div>
        <Input value={newCert} onChange={(e) => setNewCert(e.target.value)} placeholder="Ex: ICF PCC"
          className="bg-[#F8F7F4] border-[#DDD9D0] text-[#111827] rounded-xl"
          onKeyDown={(e) => { if (e.key === 'Enter' && newCert.trim()) { e.preventDefault(); setForm({ ...form, certifications: [...form.certifications, newCert.trim()] }); setNewCert(''); }}} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm text-[#4B5563] mb-1.5 block">Téléphone</label>
          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="bg-[#F8F7F4] border-[#DDD9D0] text-[#111827] rounded-xl" placeholder="+33..." />
        </div>
        <div>
          <label className="text-sm text-[#4B5563] mb-1.5 block">LinkedIn</label>
          <Input value={form.linkedin_url} onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })}
            className="bg-[#F8F7F4] border-[#DDD9D0] text-[#111827] rounded-xl" placeholder="https://..." />
        </div>
      </div>

      {/* Document uploads */}
      <DocumentUploader token={token} profile={profile} onUploaded={onSaved} />

      {/* Save button — inline on desktop */}
      <button onClick={save} disabled={saving} data-testid="save-profile-button"
        className="hidden lg:flex w-full py-3 bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#111827] shadow-[0_2px_8px_rgba(180,150,40,0.25)] rounded-full font-medium text-sm hover:bg-[#F3D060] disabled:opacity-50 transition-all items-center justify-center gap-2">
        <Save className="w-4 h-4" /> {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
      </button>
    </div>

    {/* Save button — sticky bottom on mobile */}
    <div className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#F8F7F4]/95 backdrop-blur-sm border-t border-[#DDD9D0] p-4">
      <button onClick={save} disabled={saving} data-testid="save-profile-button-mobile"
        className="w-full py-3.5 bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#111827] shadow-[0_2px_8px_rgba(180,150,40,0.25)] rounded-full font-semibold text-sm hover:bg-[#F3D060] disabled:opacity-50 transition-all shadow-lg shadow-[#D4AF37]/20 flex items-center justify-center gap-2">
        <Save className="w-4 h-4" /> {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
      </button>
    </div>
  </div>
  );
}


function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white border border-[#E8E5DE] rounded-xl shadow-sm p-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${color || '#D4AF37'}15` }}>
          <Icon className="w-5 h-5" style={{ color: color || '#D4AF37' }} />
        </div>
        <div>
          <p className="text-2xl font-light text-[#111827]">{value}</p>
          <p className="text-xs text-[#4B5563]">{label}</p>
        </div>
      </div>
    </div>
  );
}

export default function CoachDashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  // Registration form state
  const [regForm, setRegForm] = useState({
    display_name: '', bio: '', specialties: [], hourly_rate: 80,
    session_duration: 60, phone: '', linkedin_url: '', certifications: [],
  });
  const [newSpecialty, setNewSpecialty] = useState('');
  const [newCert, setNewCert] = useState('');
  const [registering, setRegistering] = useState(false);

  // Availability state
  const [slots, setSlots] = useState([]);  // Recurring weekly
  const [dateOverrides, setDateOverrides] = useState({});  // {date: [{start_time, end_time}] | null}
  const [savingSlots, setSavingSlots] = useState(false);

  // Activation dialog state
  const [showActivation, setShowActivation] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showChangePlan, setShowChangePlan] = useState(false);
  const [subActionLoading, setSubActionLoading] = useState(false);
  const [planPreviews, setPlanPreviews] = useState({}); // {planId: {type, prorata_amount, days_remaining, note}}
  const [upgradeInsights, setUpgradeInsights] = useState(null); // {current_plan, opportunities: [...]}

  // 🔒 Partage Owen — ids de bookings dont le panneau contexte est déployé
  const [openedSharedCtx, setOpenedSharedCtx] = useState({});
  const toggleSharedCtx = (id) => setOpenedSharedCtx((p) => ({ ...p, [id]: !p[id] }));

  // Date range mode for availability
  const [rangeMode, setRangeMode] = useState(false);
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [rangeSlots, setRangeSlots] = useState([{ start_time: '09:00', end_time: '17:00' }]);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const fetchProfile = useCallback(async () => {
    try {
      const res = await fetch(`${API}/coach/profile`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setProfile(data);
      if (data.has_profile) {
        setSlots(data.availability_slots || []);
        setDateOverrides(data.date_overrides || {});
      }
    } catch (err) { console.error(err); }
  }, [token]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/coach/stats`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setStats(await res.json());
    } catch (err) { console.error(err); }
  }, [token]);

  const fetchBookings = useCallback(async () => {
    try {
      const res = await fetch(`${API}/coach/bookings`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setBookings(data.bookings || []);
      }
    } catch (err) { console.error(err); }
  }, [token]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchProfile();
      setLoading(false);
    };
    if (token) init();
  }, [token, fetchProfile]);

  useEffect(() => {
    if (profile?.has_profile) {
      fetchStats();
      fetchBookings();
    }
  }, [profile, fetchStats, fetchBookings]);

  // Init tab from ?tab= query param (email deep-link)
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && ['overview', 'bookings', 'availability', 'enterprise-offers', 'settings'].includes(t)) {
      setTab(t);
    }
  }, [searchParams]);

  // Check subscription callback from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const sub = searchParams.get('subscription');
    if (sub === 'success' && sessionId && token) {
      const poll = async () => {
        try {
          const res = await fetch(`${API}/coach/subscription/status?session_id=${sessionId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const data = await res.json();
          if (data.subscription_status === 'active') {
            toast.success('Abonnement activé ! Votre profil est maintenant visible.');
            fetchProfile();
          }
        } catch (err) { console.error(err); }
      };
      // Poll a few times
      poll();
      const interval = setInterval(poll, 3000);
      setTimeout(() => clearInterval(interval), 15000);
      return () => clearInterval(interval);
    }
  }, [searchParams, token, fetchProfile]);

  const handleRegister = async () => {
    if (!regForm.display_name || !regForm.bio || regForm.specialties.length === 0) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setRegistering(true);
    try {
      const res = await fetch(`${API}/coach/register`, { method: 'POST', headers, body: JSON.stringify(regForm) });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Erreur');
      }
      toast.success('Profil coach créé !');
      await fetchProfile();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRegistering(false);
    }
  };

  const fetchSubscriptionPlans = useCallback(async () => {
    try {
      const res = await fetch(`${API}/marketplace/subscription-plans`);
      if (res.ok) {
        const data = await res.json();
        setAvailablePlans(data.plans || []);
      }
    } catch (err) { console.error('Error loading plans', err); }
  }, []);

  useEffect(() => { fetchSubscriptionPlans(); }, [fetchSubscriptionPlans]);

  const fetchPlanPreviews = useCallback(async () => {
    if (!profile?.subscription_plan || profile?.subscription_status !== 'active') return;
    const previews = {};
    const targets = availablePlans
      .map(p => p.plan_id)
      .filter(pid => pid !== profile.subscription_plan);
    await Promise.all(
      targets.map(async (pid) => {
        try {
          const res = await fetch(`${API}/coach/subscription/change-plan/preview`, {
            method: 'POST', headers,
            body: JSON.stringify({ target_plan: pid }),
          });
          if (res.ok) previews[pid] = await res.json();
        } catch (e) { /* silent */ }
      })
    );
    setPlanPreviews(previews);
  }, [profile?.subscription_plan, profile?.subscription_status, availablePlans]);

  useEffect(() => {
    if (showChangePlan) fetchPlanPreviews();
  }, [showChangePlan, fetchPlanPreviews]);

  // Upgrade insights — fetch when dialog opens (coach savings simulator)
  const fetchUpgradeInsights = useCallback(async () => {
    if (!profile?.subscription_plan || profile?.subscription_status !== 'active') return;
    try {
      const res = await fetch(`${API}/coach/subscription/upgrade-insights`, { headers });
      if (res.ok) {
        const data = await res.json();
        setUpgradeInsights(data);
      }
    } catch (e) { /* silent */ }
  }, [profile?.subscription_plan, profile?.subscription_status]);

  useEffect(() => {
    if (showChangePlan) fetchUpgradeInsights();
  }, [showChangePlan, fetchUpgradeInsights]);

  // ROI dashboard: fetch insights on mount once profile is ready
  useEffect(() => {
    if (profile?.subscription_plan && profile?.subscription_status === 'active') {
      fetchUpgradeInsights();
    }
  }, [profile?.subscription_plan, profile?.subscription_status, fetchUpgradeInsights]);

  const handleSubscribeToPlan = async (planId) => {
    setSubActionLoading(true);
    try {
      const res = await fetch(`${API}/coach/subscribe`, {
        method: 'POST', headers,
        body: JSON.stringify({ origin_url: window.location.origin, target_plan: planId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Erreur lors de la souscription');
      }
      const data = await res.json();
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.message || 'Erreur lors de la souscription');
      setSubActionLoading(false);
    }
  };

  const handleChangePlan = async (planId) => {
    setSubActionLoading(true);
    try {
      // 1. Preview (to know if upgrade [Stripe] or downgrade [DB-only])
      const preview = await fetch(`${API}/coach/subscription/change-plan/preview`, {
        method: 'POST', headers,
        body: JSON.stringify({ target_plan: planId }),
      }).then(r => r.json());

      if (preview?.type === 'same') {
        toast.info('Vous êtes déjà sur ce plan');
        setSubActionLoading(false);
        return;
      }

      // 2. Execute
      const body = { target_plan: planId };
      if (preview?.type === 'upgrade') body.origin_url = window.location.origin;

      const res = await fetch(`${API}/coach/subscription/change-plan`, {
        method: 'POST', headers,
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');

      if (data.type === 'upgrade' && data.checkout_url) {
        // Redirect to Stripe for prorata payment
        toast.success(`Redirection vers Stripe (${data.prorata_amount}€ prorata)`);
        window.location.href = data.checkout_url;
      } else {
        toast.success(data.message || 'Changement enregistré');
        setShowChangePlan(false);
        await fetchProfile();
        await fetchStats();
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubActionLoading(false);
    }
  };

  const handleCancelPendingChange = async () => {
    try {
      const res = await fetch(`${API}/coach/subscription/change-plan/cancel`, {
        method: 'POST', headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      toast.success(data.message || 'Changement annulé');
      await fetchProfile();
    } catch (err) { toast.error(err.message); }
  };

  const handleCancelSubscription = async () => {
    setSubActionLoading(true);
    try {
      const res = await fetch(`${API}/coach/subscription/cancel`, {
        method: 'POST', headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      toast.success(data.message || 'Abonnement résilié');
      setShowCancelConfirm(false);
      await fetchProfile();
      await fetchStats();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubActionLoading(false);
    }
  };

  const handleSaveSlots = async () => {
    setSavingSlots(true);
    try {
      // Clean up: remove past date overrides (keep only today and future)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStr = toLocalDateStr(today);

      const cleanedOverrides = {};
      Object.entries(dateOverrides).forEach(([date, value]) => {
        if (date >= todayStr) {
          cleanedOverrides[date] = value;
        }
      });
      setDateOverrides(cleanedOverrides);

      // Convert date_overrides to API format — only dates with slots
      const overridesList = Object.entries(cleanedOverrides)
        .filter(([, v]) => v !== null && Array.isArray(v) && v.length > 0)
        .map(([date, slotsArr]) => ({
          date,
          enabled: true,
          slots: slotsArr,
        }));
      const res = await fetch(`${API}/coach/availability`, {
        method: 'PUT', headers,
        body: JSON.stringify({ slots: [], date_overrides: overridesList }),
      });
      if (!res.ok) throw new Error('Erreur');
      toast.success('Disponibilités enregistrées');
      // Reload to confirm persistence
      await fetchProfile();
    } catch (err) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSavingSlots(false);
    }
  };

  // Calendar state
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [selectedCalDate, setSelectedCalDate] = useState(null);

  const calendarDays = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    let startDow = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    const days = [];
    for (let i = 0; i < startDow; i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push(new Date(year, month, d));
    }
    return days;
  };

  // Determine if a specific date is available (ONLY explicit overrides, no recurring fallback)
  const isDateAvailable = (date) => {
    if (!date) return false;
    const key = toLocalDateStr(date);
    // A date is available ONLY if it has an explicit override with slots
    if (key in dateOverrides) {
      return dateOverrides[key] !== null && Array.isArray(dateOverrides[key]) && dateOverrides[key].length > 0;
    }
    return false;
  };

  // Select a date to view/edit (NO toggle — just selection)
  const selectDate = (date) => {
    if (!date) return;
    setSelectedCalDate(date);
  };

  // Explicitly make a date available (adds default slot)
  const enableDate = () => {
    if (!selectedCalDate) return;
    const key = toLocalDateStr(selectedCalDate);
    setDateOverrides({ ...dateOverrides, [key]: [{ start_time: '09:00', end_time: '17:00' }] });
  };

  // Explicitly make a date unavailable (removes slots)
  const disableDate = () => {
    if (!selectedCalDate) return;
    const key = toLocalDateStr(selectedCalDate);
    const newOverrides = { ...dateOverrides };
    delete newOverrides[key];
    setDateOverrides(newOverrides);
  };

  // Get slots for the selected date (explicit overrides only)
  const getSelectedDateSlots = () => {
    if (!selectedCalDate) return [];
    const key = toLocalDateStr(selectedCalDate);
    if (key in dateOverrides && dateOverrides[key] !== null) {
      return dateOverrides[key];
    }
    return [];
  };

  // Update a slot for the selected date (creates override if needed)
  const updateSelectedDateSlot = (idx, field, value) => {
    if (!selectedCalDate) return;
    const key = toLocalDateStr(selectedCalDate);
    const currentSlots = [...getSelectedDateSlots()];
    currentSlots[idx] = { ...currentSlots[idx], [field]: value };
    setDateOverrides({ ...dateOverrides, [key]: currentSlots });
  };

  const addSelectedDateSlot = () => {
    if (!selectedCalDate) return;
    const key = toLocalDateStr(selectedCalDate);
    const currentSlots = [...getSelectedDateSlots()];
    currentSlots.push({ start_time: '09:00', end_time: '17:00' });
    setDateOverrides({ ...dateOverrides, [key]: currentSlots });
  };

  const removeSelectedDateSlot = (idx) => {
    if (!selectedCalDate) return;
    const key = toLocalDateStr(selectedCalDate);
    const currentSlots = getSelectedDateSlots().filter((_, i) => i !== idx);
    if (currentSlots.length > 0) {
      setDateOverrides({ ...dateOverrides, [key]: currentSlots });
    } else {
      // No more slots → date becomes unavailable
      const newOverrides = { ...dateOverrides };
      delete newOverrides[key];
      setDateOverrides(newOverrides);
    }
  };

  const applyDateRange = () => {
    if (!rangeStart || !rangeEnd || rangeSlots.length === 0) return;
    const newOverrides = { ...dateOverrides };
    const start = new Date(rangeStart);
    const end = new Date(rangeEnd);
    let count = 0;
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const key = toLocalDateStr(d);
      newOverrides[key] = rangeSlots.map(s => ({ ...s }));
      count++;
    }
    setDateOverrides(newOverrides);
    setRangeMode(false);
    toast.success(`Créneaux appliqués sur ${count} jours`);
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      const res = await fetch(`${API}/coach/bookings/${bookingId}/status`, {
        method: 'PUT', headers, body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      if (status === 'cancelled' && data.refund_status === 'refunded') {
        toast.success(`Séance annulée, remboursement de ${data.refund_amount}€ déclenché`);
      } else {
        toast.success(`Réservation ${status === 'completed' ? 'terminée' : 'annulée'}`);
      }
      fetchBookings();
      fetchStats();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // ─── Booking lifecycle (coach side) ───
  const [coachRescheduleBooking, setCoachRescheduleBooking] = useState(null);
  const [coachCancelDialog, setCoachCancelDialog] = useState(null);
  const [coachRescheduleMessage, setCoachRescheduleMessage] = useState('');

  const handleCoachRescheduleRequest = async ({ new_date, new_start_time }) => {
    const res = await fetch(
      `${API}/coach/bookings/${coachRescheduleBooking.booking_id}/reschedule-request`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          new_date,
          new_start_time,
          message: coachRescheduleMessage || null,
        }),
      },
    );
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Erreur');
    toast.success('Demande de report envoyée au client');
    setCoachRescheduleMessage('');
    fetchBookings();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F7F4] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full" />
      </div>
    );
  }

  // Registration flow
  if (!profile?.has_profile) {
    return (
      <div className="min-h-screen bg-[#F8F7F4] py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <p className="uppercase text-xs tracking-[0.2em] text-[#D4AF37] mb-4">Devenir coach</p>
          <h1 className="text-3xl sm:text-4xl font-light text-[#111827] tracking-tight mb-2">
            Créez votre profil coach
          </h1>
          <p className="text-[#4B5563] mb-8">Rejoignez notre Espace Coachs et proposez vos services de coaching.</p>

          <div className="space-y-6">
            <div>
              <label className="text-sm font-medium text-[#4B5563] mb-2 block">Nom affiché *</label>
              <Input data-testid="reg-display-name" value={regForm.display_name}
                onChange={(e) => setRegForm({...regForm, display_name: e.target.value})}
                className="bg-white border-[#DDD9D0] text-[#111827]" placeholder="Ex: Marie Dupont" />
            </div>

            <div>
              <label className="text-sm font-medium text-[#4B5563] mb-2 block">Bio *</label>
              <Textarea data-testid="reg-bio" value={regForm.bio}
                onChange={(e) => setRegForm({...regForm, bio: e.target.value})}
                className="bg-white border-[#DDD9D0] text-[#111827] min-h-[120px]"
                placeholder="Décrivez votre expérience, approche et philosophie de coaching..." />
            </div>

            <div>
              <label className="text-sm font-medium text-[#4B5563] mb-2 block">Spécialités * — sélectionnées : {regForm.specialties.length}</label>
              <div className="flex flex-wrap gap-2 mb-3">
                {regForm.specialties.map((s, i) => (
                  <Badge key={i} className="bg-[#D4AF37]/15 text-[#92710A] border-[#D4AF37]/30 cursor-pointer hover:bg-red-500/20 hover:text-red-400 transition-colors"
                    onClick={() => setRegForm({...regForm, specialties: regForm.specialties.filter((_, j) => j !== i)})}>
                    {s} <X className="w-3 h-3 ml-1" />
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={newSpecialty} onChange={(e) => setNewSpecialty(e.target.value)}
                  className="bg-white border-[#DDD9D0] text-[#111827]" placeholder="Ajouter une spécialité"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newSpecialty.trim()) {
                      e.preventDefault();
                      setRegForm({...regForm, specialties: [...regForm.specialties, newSpecialty.trim()]});
                      setNewSpecialty('');
                    }
                  }} />
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {SPECIALTIES_SUGGESTIONS.filter(s => !regForm.specialties.includes(s)).slice(0, 6).map(s => (
                  <button key={s} onClick={() => setRegForm({...regForm, specialties: [...regForm.specialties, s]})}
                    className="px-2 py-1 rounded text-xs bg-white/5 text-[#4B5563] hover:text-[#111827] hover:bg-white/10 transition-all">
                    + {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#4B5563] mb-2 block">Tarif horaire (€) *</label>
                <Input data-testid="reg-rate" type="number" value={regForm.hourly_rate}
                  onChange={(e) => setRegForm({...regForm, hourly_rate: parseFloat(e.target.value)})}
                  className="bg-white border-[#DDD9D0] text-[#111827]" min="1" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#4B5563] mb-2 block">Durée session (min)</label>
                <select data-testid="reg-duration" value={regForm.session_duration}
                  onChange={(e) => setRegForm({...regForm, session_duration: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 bg-white border border-[#DDD9D0] rounded-md text-[#111827] text-sm">
                  <option value={30}>30 min</option>
                  <option value={45}>45 min</option>
                  <option value={60}>60 min</option>
                  <option value={90}>90 min</option>
                  <option value={120}>120 min</option>
                </select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-[#4B5563] mb-2 block">Certifications</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {regForm.certifications.map((c, i) => (
                  <Badge key={i} variant="outline" className="bg-white/5 text-[#4B5563] border-[#DDD9D0] cursor-pointer hover:bg-red-500/20"
                    onClick={() => setRegForm({...regForm, certifications: regForm.certifications.filter((_, j) => j !== i)})}>
                    {c} <X className="w-3 h-3 ml-1" />
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={newCert} onChange={(e) => setNewCert(e.target.value)}
                  className="bg-white border-[#DDD9D0] text-[#111827]" placeholder="Ex: ICF PCC"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newCert.trim()) {
                      e.preventDefault();
                      setRegForm({...regForm, certifications: [...regForm.certifications, newCert.trim()]});
                      setNewCert('');
                    }
                  }} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#4B5563] mb-2 block">Téléphone</label>
                <Input value={regForm.phone} onChange={(e) => setRegForm({...regForm, phone: e.target.value})}
                  className="bg-white border-[#DDD9D0] text-[#111827]" placeholder="+33..." />
              </div>
              <div>
                <label className="text-sm font-medium text-[#4B5563] mb-2 block">LinkedIn</label>
                <Input value={regForm.linkedin_url} onChange={(e) => setRegForm({...regForm, linkedin_url: e.target.value})}
                  className="bg-white border-[#DDD9D0] text-[#111827]" placeholder="https://linkedin.com/in/..." />
              </div>
            </div>

            <button data-testid="register-coach-button" onClick={handleRegister} disabled={registering}
              className="w-full py-3 bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#111827] shadow-[0_2px_8px_rgba(180,150,40,0.25)] rounded-full font-medium hover:bg-[#F3D060] disabled:opacity-50 transition-all">
              {registering ? 'Création...' : 'Créer mon profil coach'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard
  const MONTH_NAMES = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
  const selectedDaySlots = getSelectedDateSlots();
  const selectedDow = selectedCalDate ? (selectedCalDate.getDay() === 0 ? 6 : selectedCalDate.getDay() - 1) : null;

  return (
    <div className="theme-light min-h-screen pt-16 sm:pt-8 pb-28 sm:pb-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Back button + Header */}
        <BackButton to="/marketplace" />

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <p className="uppercase text-xs tracking-[0.2em] text-[#92710A] mb-1">Dashboard Coach</p>
            <h1 className="text-xl sm:text-2xl font-light text-[#111827]">{profile.display_name}</h1>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {profile.approval_status === 'pending_documents' && (
              <Badge className="bg-orange-500/10 text-orange-400 border-orange-500/20">Justificatifs requis</Badge>
            )}
            {profile.approval_status === 'pending_approval' && (
              <Badge className="bg-yellow-500/10 text-yellow-400 border-yellow-500/20">En attente de validation</Badge>
            )}
            {profile.approval_status === 'rejected' && (
              <Badge className="bg-red-500/10 text-red-400 border-red-500/20">Dossier refusé</Badge>
            )}
            {profile.approval_status === 'approved' && profile.subscription_status === 'active' ? (
              <>
                <Badge className="bg-green-500/10 text-green-700 border-green-500/20">Actif</Badge>
                {profile.subscription_plan && <CoachPlanBadge planId={profile.subscription_plan} size="lg" />}
              </>
            ) : profile.approval_status === 'approved' && profile.subscription_status === 'cancelled' ? (
              <>
                <Badge className="bg-zinc-100 text-zinc-600 border-zinc-200">Résilié</Badge>
                <button data-testid="reactivate-coach-button" onClick={() => setShowActivation(true)}
                  className="px-5 py-2 bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#111827] shadow-[0_2px_8px_rgba(180,150,40,0.25)] rounded-full font-medium text-sm hover:bg-[#F3D060] transition-all">
                  Réactiver
                </button>
              </>
            ) : profile.approval_status === 'approved' && (
              <button data-testid="activate-coach-button" onClick={() => setShowActivation(true)}
                className="px-5 py-2 bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#111827] shadow-[0_2px_8px_rgba(180,150,40,0.25)] rounded-full font-medium text-sm hover:bg-[#F3D060] transition-all">
                Choisir mon plan
              </button>
            )}
          </div>
        </div>

        {/* Pending approval banner */}
        {profile.approval_status === 'pending_approval' && (
          <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-xl p-5 mb-6">
            <p className="text-[#D4AF37] font-medium text-sm mb-1">Dossier en cours de vérification</p>
            <p className="text-[#4B5563] text-sm">
              Votre dossier est en attente d'approbation par notre équipe (24-48h). Vous recevrez un email de confirmation.
            </p>
          </div>
        )}

        {/* Rejected banner */}
        {profile.approval_status === 'rejected' && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-5 mb-6">
            <p className="text-red-400 font-medium text-sm mb-1">Dossier non validé</p>
            <p className="text-[#4B5563] text-sm">
              {profile.rejection_reason || 'Veuillez mettre à jour vos documents et re-soumettre votre dossier.'}
            </p>
          </div>
        )}

        {/* Pending documents banner */}
        {profile.approval_status === 'pending_documents' && (
          <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-5 mb-6">
            <p className="text-orange-400 font-medium text-sm mb-1">Justificatifs manquants</p>
            <p className="text-[#4B5563] text-sm">
              Déposez vos 4 justificatifs (photo, pièce d'identité, certificat coach, justificatif d'exercice professionnel) pour soumettre votre dossier.
            </p>
          </div>
        )}

        {/* Activation choice panel — 3 plans — only for approved coaches */}
        {showActivation && profile.approval_status === 'approved' && (
          <div className="bg-white border border-[#D4AF37]/30 rounded-2xl p-5 sm:p-6 mb-6 shadow-sm" data-testid="activation-choice-panel">
            <div className="flex items-start justify-between mb-4 gap-3">
              <div>
                <p className="text-[#111827] font-display font-medium text-base">Choisissez votre plan</p>
                <p className="text-[#4B5563] text-xs mt-0.5 font-body">Modèle hybride : abonnement mensuel + commission par session.</p>
              </div>
              <button onClick={() => setShowActivation(false)} className="text-[#4B5563] hover:text-[#111827] p-1" data-testid="close-activation">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {availablePlans.map(plan => {
                const style = PLAN_TIER_STYLES[plan.plan_id];
                if (!style) return null;
                const { Icon, accent } = style;
                return (
                  <button
                    key={plan.plan_id}
                    data-testid={`select-plan-${plan.plan_id}`}
                    onClick={() => handleSubscribeToPlan(plan.plan_id)}
                    disabled={subActionLoading}
                    className={`relative p-4 rounded-xl border text-left transition-all hover:shadow-md disabled:opacity-50 ${
                      plan.is_popular ? 'border-[#D4AF37] bg-[#FEF9E7]/50 shadow-sm' : 'border-[#DDD9D0] bg-white hover:border-[#D4AF37]/50'
                    }`}
                  >
                    {plan.is_popular && (
                      <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-[#D4AF37] text-[#111827] text-[9px] font-semibold rounded-full tracking-wide">
                        RECOMMANDÉ
                      </div>
                    )}
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="w-4 h-4" style={{ color: accent }} />
                      <p className="text-[#111827] font-medium text-sm">{plan.label}</p>
                    </div>
                    <p className="text-[#111827] text-2xl font-light leading-tight">{plan.price_eur}€<span className="text-xs text-[#4B5563] font-normal">/mois</span></p>
                    <p className="text-[#4B5563] text-[11px] mt-1">+ {plan.commission_rate}% de commission</p>
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-[#4B5563] mt-3 text-center font-body">
              Vous pourrez changer de plan ou résilier à tout moment depuis votre dashboard.
            </p>
          </div>
        )}

        {/* Warning if approved but not yet activated */}
        {!showActivation && profile.approval_status === 'approved' && profile.subscription_status === 'pending' && (
          <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-xl p-4 mb-6">
            <p className="text-[#92710A] text-sm">
              Votre profil est approuvé ! Choisissez votre plan d'abonnement pour apparaître sur la marketplace.
            </p>
          </div>
        )}

        {/* Cancelled banner */}
        {profile.approval_status === 'approved' && profile.subscription_status === 'cancelled' && !showActivation && (
          <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-4 mb-6">
            <p className="text-zinc-700 text-sm">
              Votre abonnement est résilié. Votre profil n'apparaît plus sur la marketplace. Vous pouvez le réactiver à tout moment.
            </p>
          </div>
        )}

        {/* Mon abonnement — carte pour les coachs actifs */}
        {profile.approval_status === 'approved' && profile.subscription_status === 'active' && profile.subscription_plan && (
          <div className="bg-white border border-[#E8E5DE] rounded-2xl p-5 sm:p-6 mb-6 shadow-sm" data-testid="my-subscription-card">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-start gap-4">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background: `${PLAN_TIER_STYLES[profile.subscription_plan]?.accent || '#D4AF37'}15`,
                    color: PLAN_TIER_STYLES[profile.subscription_plan]?.accent || '#D4AF37',
                  }}
                >
                  {(() => {
                    const Icon = PLAN_TIER_STYLES[profile.subscription_plan]?.Icon || Sparkles;
                    return <Icon className="w-6 h-6" />;
                  })()}
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-[#111827] font-display font-medium text-base">
                      Plan {PLAN_TIER_STYLES[profile.subscription_plan]?.label || profile.subscription_plan}
                    </p>
                    <CoachPlanBadge planId={profile.subscription_plan} />
                  </div>
                  <p className="text-[#4B5563] text-xs mt-0.5 font-body">
                    Commission : {stats?.commission_rate ?? '—'}% · {profile.subscription_end_date ? `Renouvellement : ${new Date(profile.subscription_end_date).toLocaleDateString('fr-FR')}` : 'Abonnement actif'}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => setShowChangePlan(true)}
                  data-testid="change-plan-button"
                  className="px-4 py-2 rounded-full text-xs font-medium border border-[#DDD9D0] bg-white text-[#111827] hover:border-[#D4AF37] hover:bg-[#FEF9E7] transition-all"
                >
                  Changer de plan
                </button>
                <button
                  onClick={() => setShowCancelConfirm(true)}
                  data-testid="cancel-subscription-button"
                  className="px-4 py-2 rounded-full text-xs font-medium border border-red-200 bg-white text-red-600 hover:bg-red-50 transition-all"
                >
                  Résilier
                </button>
              </div>
            </div>

            {/* Pending plan change banner (downgrade scheduled) */}
            {profile.pending_plan_change?.target_plan && (
              <div
                data-testid="pending-plan-change-banner"
                className="mt-4 pt-4 border-t border-[#E8E5DE] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
              >
                <div className="flex items-center gap-2 text-sm">
                  <CalendarClock className="w-4 h-4 text-[#92710A]" />
                  <span className="text-[#111827]">
                    Changement programmé : passage en <strong>{PLAN_TIER_STYLES[profile.pending_plan_change.target_plan]?.label || profile.pending_plan_change.target_plan}</strong>
                    {profile.pending_plan_change.scheduled_at && (
                      <> le <strong>{new Date(profile.pending_plan_change.scheduled_at).toLocaleDateString('fr-FR')}</strong></>
                    )}
                  </span>
                </div>
                <button
                  onClick={handleCancelPendingChange}
                  data-testid="cancel-pending-change-button"
                  className="text-xs text-[#4B5563] underline hover:text-[#111827]"
                >
                  Annuler ce changement
                </button>
              </div>
            )}
          </div>
        )}

        {/* Tabs — scrollable on mobile */}
        <Tabs value={tab} onValueChange={setTab}>
          <div className="overflow-x-auto -mx-4 px-4 mb-6 scrollbar-hide">
            <TabsList className="bg-white border border-[#E8E5DE] inline-flex w-auto min-w-max">
              <TabsTrigger value="overview" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B] whitespace-nowrap text-sm px-3 py-2">
                <BarChart3 className="w-4 h-4 mr-1.5" /> Vue d'ensemble
              </TabsTrigger>
              <TabsTrigger value="bookings" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B] whitespace-nowrap text-sm px-3 py-2">
                <Calendar className="w-4 h-4 mr-1.5" /> Réservations
              </TabsTrigger>
              <TabsTrigger value="availability" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B] whitespace-nowrap text-sm px-3 py-2">
                <Clock className="w-4 h-4 mr-1.5" /> Disponibilités
              </TabsTrigger>
              <TabsTrigger value="enterprise-offers" data-testid="tab-enterprise-offers"
                className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B] whitespace-nowrap text-sm px-3 py-2">
                <Building2 className="w-4 h-4 mr-1.5" /> Offres entreprise
              </TabsTrigger>
              <TabsTrigger value="insights-clients" data-testid="tab-insights-clients"
                onClick={() => (window.location.href = '/coach/insights-clients')}
                className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B] whitespace-nowrap text-sm px-3 py-2">
                <Sparkles className="w-4 h-4 mr-1.5" /> COACHDIA Prism clients
              </TabsTrigger>
              <TabsTrigger value="payouts" data-testid="tab-payouts" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B] whitespace-nowrap text-sm px-3 py-2">
                <CreditCard className="w-4 h-4 mr-1.5" /> Paiements
              </TabsTrigger>
              <TabsTrigger value="settings" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B] whitespace-nowrap text-sm px-3 py-2">
                <Settings className="w-4 h-4 mr-1.5" /> Profil
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Overview tab */}
          <TabsContent value="overview">
            {stats && (
              <>
                {/* Activité — sessions */}
                <div className="mb-5">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#4B5563] font-medium mb-2">Activité</p>
                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    <StatCard label="Sessions totales" value={stats.total_bookings} icon={Calendar} />
                    <StatCard label="Confirmées" value={stats.confirmed} icon={Check} />
                    <StatCard label="Terminées" value={stats.completed} icon={BarChart3} />
                    <StatCard label="Annulées" value={stats.cancelled || 0} icon={X} color="#64748B" />
                  </div>
                </div>
                {/* Revenus — CA · commissions · revenu net */}
                <div className="mb-5" data-testid="revenue-section">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#4B5563] font-medium mb-2">Revenus</p>
                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    <StatCard label="Chiffre d'affaires" value={`${stats.total_revenue || 0}€`} icon={Euro} />
                    <StatCard label={`Commissions (${stats.commission_rate ?? '—'}%)`} value={`${stats.total_commissions || 0}€`} icon={Percent} color="#6366F1" />
                    <StatCard label="Revenu net" value={`${((stats.total_revenue || 0) - (stats.total_commissions || 0)).toFixed(2)}€`} icon={Check} color="#10B981" />
                    <StatCard label="À recevoir" value={`${stats.pending_payout || 0}€`} icon={Clock} color="#f97316" />
                  </div>
                </div>

                {/* ROI Coach — Projection Elite dynamique */}
                {(() => {
                  if (!upgradeInsights || !Array.isArray(upgradeInsights.opportunities) || upgradeInsights.opportunities.length === 0) return null;
                  const best = upgradeInsights.opportunities[0];
                  if (!best) return null;
                  const current30dRevenue = upgradeInsights.last_30d_revenue || 0;
                  const current30dSessions = upgradeInsights.last_30d_sessions || 0;
                  const currentNet = Math.max(0, current30dRevenue - (current30dRevenue * (upgradeInsights.current_commission_rate || 0) / 100) - (upgradeInsights.current_price || 0));
                  const projectedNet = Math.max(0, current30dRevenue - (current30dRevenue * (best.target_commission_rate || 0) / 100) - (best.target_price || 0));
                  const isRecommended = best.recommendation === 'recommended';
                  const isNeutral = best.recommendation === 'neutral';
                  return (
                    <div className="mb-5" data-testid="coach-roi-section">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-[#4B5563] font-medium mb-2">ROI & Projection</p>
                      <div className={`rounded-xl border p-5 sm:p-6 shadow-sm ${isRecommended ? 'border-[#D4AF37]/50 bg-gradient-to-br from-[#FFFBEA] to-white' : 'border-[#E8E5DE] bg-white'}`} data-testid="coach-roi-card">
                        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] tracking-[0.25em] uppercase text-[#92710A] font-semibold">Simulation · {best.label}</span>
                              {isRecommended && (
                                <span className="text-[10px] px-2 py-0.5 bg-[#D4AF37] text-[#0A0A0B] rounded-full font-semibold tracking-wide" data-testid="coach-roi-badge-reco">RENTABLE</span>
                              )}
                              {isNeutral && (
                                <span className="text-[10px] px-2 py-0.5 bg-[#F3E5A8] text-[#92710A] rounded-full font-semibold tracking-wide" data-testid="coach-roi-badge-neutral">PROCHE DU SEUIL</span>
                              )}
                              {!isRecommended && !isNeutral && (
                                <span className="text-[10px] px-2 py-0.5 bg-stone-100 text-stone-600 rounded-full font-semibold tracking-wide" data-testid="coach-roi-badge-notyet">PAS ENCORE RENTABLE</span>
                              )}
                            </div>
                            <p className="text-sm text-[#4B5563] mt-1" data-testid="coach-roi-message">{best.message}</p>
                          </div>
                          <button
                            onClick={() => setShowChangePlan(true)}
                            data-testid="coach-roi-cta"
                            className="px-4 py-2 text-sm font-medium rounded-full bg-[#0F0F10] text-white hover:bg-[#1C1C1E] transition"
                          >
                            Passer en {best.label} →
                          </button>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                          <div className="p-3 rounded-lg bg-white border border-stone-200" data-testid="coach-roi-current-revenue">
                            <p className="text-[10px] uppercase tracking-widest text-[#4B5563]">CA 30j</p>
                            <p className="text-lg sm:text-xl font-semibold text-[#111827] mt-1">{current30dRevenue.toFixed(0)}€</p>
                            <p className="text-[10px] text-stone-500 mt-0.5">{current30dSessions} session(s)</p>
                          </div>
                          <div className="p-3 rounded-lg bg-white border border-stone-200" data-testid="coach-roi-current-net">
                            <p className="text-[10px] uppercase tracking-widest text-[#4B5563]">Net actuel (estim.)</p>
                            <p className="text-lg sm:text-xl font-semibold text-[#111827] mt-1">{currentNet.toFixed(0)}€</p>
                            <p className="text-[10px] text-stone-500 mt-0.5">{(upgradeInsights.current_commission_rate || 0).toFixed(0)}% comm · {(upgradeInsights.current_price || 0).toFixed(0)}€/mois</p>
                          </div>
                          <div className={`p-3 rounded-lg border ${isRecommended ? 'bg-[#FFFBEA] border-[#D4AF37]/40' : 'bg-white border-stone-200'}`} data-testid="coach-roi-projected-net">
                            <p className="text-[10px] uppercase tracking-widest text-[#4B5563]">Net projeté en {best.label}</p>
                            <p className={`text-lg sm:text-xl font-semibold mt-1 ${isRecommended ? 'text-[#92710A]' : 'text-[#111827]'}`}>{projectedNet.toFixed(0)}€</p>
                            <p className="text-[10px] text-stone-500 mt-0.5">{best.target_commission_rate}% comm · {best.target_price?.toFixed(0) ?? '—'}€/mois</p>
                          </div>
                          <div className={`p-3 rounded-lg border ${best.projected_monthly_savings > 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-stone-50 border-stone-200'}`} data-testid="coach-roi-gain">
                            <p className="text-[10px] uppercase tracking-widest text-[#4B5563]">Gain potentiel / mois</p>
                            <p className={`text-lg sm:text-xl font-semibold mt-1 ${best.projected_monthly_savings > 0 ? 'text-emerald-700' : 'text-stone-600'}`}>
                              {best.projected_monthly_savings > 0 ? '+' : ''}{(best.projected_monthly_savings || 0).toFixed(0)}€
                            </p>
                            <p className="text-[10px] text-stone-500 mt-0.5">
                              Seuil : {best.break_even_sessions == null ? '—' : `${best.break_even_sessions} session(s)`}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
                {/* Encaissements */}
                <div className="mb-6">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#4B5563] font-medium mb-2">Encaissements</p>
                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    <StatCard label="Reçu" value={`${stats.total_paid || 0}€`} icon={Check} color="#22c55e" />
                    <StatCard label="Sessions payées" value={stats.total_paid_count || 0} icon={Euro} color="#22c55e" />
                  </div>
                </div>
              </>
            )}
            <div className="bg-white border border-[#E8E5DE] rounded-xl shadow-sm p-4 sm:p-6">
              <h3 className="text-[#111827] font-medium mb-4">Prochaines sessions</h3>
              {(() => {
                const now = new Date();
                const upcoming = bookings.filter(b => {
                  const isActive = b.status === 'confirmed' || b.status === 'pending_payment' || b.status === 'pending';
                  const isFuture = new Date(`${b.date}T${b.end_time || '23:59'}`) >= now;
                  return isActive && isFuture;
                });
                return upcoming.length === 0 ? (
                  <p className="text-[#4B5563] text-sm">Aucune session à venir.</p>
                ) : (
                <div className="space-y-3">
                  {upcoming.slice(0, 5).map(b => {
                    const now = new Date();
                    const sessionStart = new Date(`${b.date}T${b.start_time}`);
                    const sessionEnd = new Date(`${b.date}T${b.end_time}`);
                    const earlyJoin = new Date(sessionStart.getTime() - 10 * 60 * 1000);
                    const joinable = ['confirmed', 'pending_payment'].includes(b.status) && now >= earlyJoin && now <= sessionEnd;
                    const linkVisible = ['confirmed', 'pending_payment'].includes(b.status);

                    return (
                    <div key={b.booking_id} className={`flex items-center justify-between p-3 rounded-lg gap-3 ${joinable || linkVisible ? 'bg-[#D4AF37]/5 border border-[#D4AF37]/20' : 'bg-white/5'}`}>
                      <div className="min-w-0">
                        <p className="text-[#111827] text-sm truncate">{b.client_name || b.client_email}</p>
                        <p className="text-[#4B5563] text-xs">{formatDateFR(b.date)} à {formatTimeFR(b.start_time)}</p>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        {(joinable || linkVisible) && (
                          <button data-testid={`coach-join-session-${b.booking_id}`}
                            onClick={() => navigate(`/session/${b.booking_id}`)}
                            className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#111827] shadow-[0_2px_8px_rgba(180,150,40,0.25)] rounded-lg text-xs font-medium hover:bg-[#F3D060] transition-all">
                            <Video className="w-3.5 h-3.5" /> Rejoindre
                          </button>
                        )}
                        <button onClick={() => setCoachRescheduleBooking(b)}
                          data-testid={`coach-reschedule-overview-${b.booking_id}`}
                          className="p-2 rounded-lg bg-violet-50 text-[#6D28D9] hover:bg-violet-100 transition-all" title="Demander un report">
                          <CalendarClock className="w-4 h-4" />
                        </button>
                        <button onClick={() => setCoachCancelDialog(b)}
                          data-testid={`coach-cancel-overview-${b.booking_id}`}
                          className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all" title="Annuler (remboursement client)">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              );
              })()}
            </div>
          </TabsContent>

          {/* Bookings tab — mobile cards, desktop table */}
          <TabsContent value="bookings">
            {/* Règles d'annulation / report — rappel permanent côté coach */}
            <RefundRulesCard className="mb-4" />

            {bookings.length === 0 ? (
              <div className="bg-white border border-[#E8E5DE] rounded-xl shadow-sm p-12 text-center">
                <p className="text-[#4B5563] text-sm">Aucune réservation pour le moment.</p>
              </div>
            ) : (
              <>
                {/* Mobile cards */}
                <div className="space-y-3 sm:hidden">
                  {bookings.map(b => {
                    const now = new Date();
                    const sessionStart = new Date(`${b.date}T${b.start_time}`);
                    const sessionEnd = new Date(`${b.date}T${b.end_time}`);
                    const earlyJoin = new Date(sessionStart.getTime() - 10 * 60 * 1000);
                    const joinable = ['confirmed', 'pending_payment'].includes(b.status) && now >= earlyJoin && now <= sessionEnd;
                    const canAccess = ['confirmed', 'pending_payment'].includes(b.status) && sessionStart > now;

                    return (
                    <div key={b.booking_id} className={`bg-white border rounded-xl p-4 ${joinable || canAccess ? 'border-[#D4AF37]/20' : 'border-[#E8E5DE]'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[#111827] text-sm font-medium truncate">{b.client_name || b.client_email}</span>
                        <StatusBadge booking={b} />
                      </div>
                      {/* 🎖️ Iter384 Lot B — Badge "Présence certifiée" */}
                      {b.presence_proof?.both_joined && b.presence_proof?.overlap_ok && (
                        <span
                          data-testid={`presence-certified-coach-${b.booking_id}`}
                          title={`Overlap ${b.presence_proof.overlap_seconds || 0}s · min ${b.presence_proof.min_required || 0}s`}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#FFFCEC] border border-[#E8DDB5] text-[#92710A] mb-2"
                        >
                          ✓ Présence certifiée · {Math.round((b.presence_proof.overlap_seconds || 0) / 60)} min
                        </span>
                      )}
                      {b.theme_label && (
                        <div className="flex items-center gap-1.5 mb-2">
                          <Tag className="w-3 h-3 text-[#8B5CF6]" />
                          <span className="text-xs font-medium text-[#8B5CF6]">{b.theme_label}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-4 text-xs text-[#4B5563] mb-3">
                        <span>{formatDateFR(b.date)}</span>
                        <span>{formatTimeFR(b.start_time)} – {formatTimeFR(b.end_time)}</span>
                        <span className="text-[#92710A] font-medium">{b.amount}€</span>
                      </div>
                      <div className="flex gap-2">
                        {(joinable || canAccess) && (
                          <button onClick={() => navigate(`/session/${b.booking_id}`)}
                            className="flex-1 py-2 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#111827] shadow-[0_2px_8px_rgba(180,150,40,0.25)] text-xs font-medium flex items-center justify-center gap-1.5">
                            <Video className="w-3.5 h-3.5" /> Rejoindre
                          </button>
                        )}
                        {/* 🔒 Iter383 — Reporter/Annuler uniquement si session FUTURE (date > now).
                            Les sessions passées en confirmed ne doivent plus afficher ces actions. */}
                        {['confirmed', 'pending_payment'].includes(b.status) && sessionStart > now && (
                          <>
                            {b.status === 'confirmed' && b.reschedule_request?.status !== 'pending' && b.reschedule_count < 1 && (
                              <button onClick={() => setCoachRescheduleBooking(b)}
                                data-testid={`coach-reschedule-mobile-${b.booking_id}`}
                                className="flex-1 py-2 rounded-lg bg-violet-50 text-[#6D28D9] text-xs font-medium">Reporter</button>
                            )}
                            <button onClick={() => setCoachCancelDialog(b)}
                              data-testid={`coach-cancel-mobile-${b.booking_id}`}
                              className="flex-1 py-2 rounded-lg bg-red-500/10 text-red-400 text-xs font-medium">Annuler</button>
                          </>
                        )}
                        {b.status === 'completed' && b.ai_report && (
                          <button data-testid={`coach-download-pdf-${b.booking_id}`}
                            onClick={async () => {
                              try {
                                const res = await fetch(`${API}/marketplace/sessions/${b.booking_id}/report/pdf`, { headers: { Authorization: `Bearer ${token}` } });
                                if (!res.ok) throw new Error('Erreur');
                                const blob = await res.blob();
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a'); a.href = url; a.download = `rapport_${b.date}.pdf`;
                                document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
                              } catch (err) { toast.error('Erreur PDF'); }
                            }}
                            className="flex items-center gap-1.5 py-2 px-3 rounded-lg bg-[#D4AF37]/15 text-[#92710A] text-xs font-medium">
                            <Download className="w-3.5 h-3.5" /> PDF
                          </button>
                        )}
                      </div>
                      {/* 🔒 Bouton contexte Owen partagé (mobile) */}
                      {['confirmed', 'completed'].includes(b.status) && (
                        <button
                          onClick={() => toggleSharedCtx(b.booking_id)}
                          data-testid={`coach-shared-ctx-toggle-${b.booking_id}`}
                          className="mt-3 w-full py-2 rounded-lg text-xs font-medium border"
                          style={{
                            background: '#FFFCEC',
                            borderColor: '#E8E2D2',
                            color: '#B7901F',
                          }}
                        >
                          🔒 {openedSharedCtx[b.booking_id] ? 'Masquer' : 'Voir'} le contexte Owen partagé
                        </button>
                      )}
                      {openedSharedCtx[b.booking_id] && (
                        <div className="mt-3">
                          <CoachBookingSharedContext bookingId={b.booking_id} />
                        </div>
                      )}
                    </div>
                    );
                  })}

                {/* Desktop table */}
                </div>
                <div className="hidden sm:block bg-white border border-[#E8E5DE] rounded-xl shadow-sm overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#E8E5DE]">
                        <th className="text-left text-xs text-[#4B5563] uppercase tracking-wider px-4 py-3">Client</th>
                        <th className="text-left text-xs text-[#4B5563] uppercase tracking-wider px-4 py-3">Th&egrave;me</th>
                        <th className="text-left text-xs text-[#4B5563] uppercase tracking-wider px-4 py-3">Date</th>
                        <th className="text-left text-xs text-[#4B5563] uppercase tracking-wider px-4 py-3">Heure</th>
                        <th className="text-left text-xs text-[#4B5563] uppercase tracking-wider px-4 py-3">Montant</th>
                        <th className="text-left text-xs text-[#4B5563] uppercase tracking-wider px-4 py-3">Statut</th>
                        <th className="text-left text-xs text-[#4B5563] uppercase tracking-wider px-4 py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map(b => (
                        <React.Fragment key={b.booking_id}>
                        <tr className="border-b border-[#E8E5DE] hover:bg-white/[0.02]">
                          <td className="px-4 py-3 text-sm text-[#111827]">{b.client_name || b.client_email}</td>
                          <td className="px-4 py-3 text-sm">
                            {b.theme_label ? (
                              <span className="inline-flex items-center gap-1 text-[#8B5CF6] font-medium">
                                <Tag className="w-3 h-3" /> {b.theme_label}
                              </span>
                            ) : (
                              <span className="text-[#4B5563]">&mdash;</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-[#4B5563]">{formatDateFR(b.date)}</td>
                          <td className="px-4 py-3 text-sm text-[#4B5563]">{formatTimeFR(b.start_time)} – {formatTimeFR(b.end_time)}</td>
                          <td className="px-4 py-3 text-sm text-[#92710A] font-medium">{b.amount}€</td>
                          <td className="px-4 py-3">
                            <StatusBadge booking={b} />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1">
                              {['confirmed', 'pending_payment'].includes(b.status) && (() => {
                                const now = new Date();
                                const ss = new Date(`${b.date}T${b.start_time}`);
                                const se = new Date(`${b.date}T${b.end_time}`);
                                const ej = new Date(ss.getTime() - 10 * 60 * 1000);
                                const joinable = now >= ej && now <= se;
                                const canAccess = ss > now;
                                return (
                                  <>
                                    {(joinable || canAccess) && (
                                      <button onClick={() => navigate(`/session/${b.booking_id}`)}
                                        className="p-1.5 rounded bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#111827] shadow-[0_2px_8px_rgba(180,150,40,0.25)] hover:bg-[#F3D060]" title="Rejoindre">
                                        <Video className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                    {/* 🔒 Iter383 — Reporter/Annuler uniquement sur sessions FUTURES */}
                                    {ss > now && b.status === 'confirmed' && b.reschedule_count < 1 && (
                                      <button onClick={() => setCoachRescheduleBooking(b)}
                                        data-testid={`coach-reschedule-table-${b.booking_id}`}
                                        className="p-1.5 rounded text-[#6D28D9] hover:bg-violet-50" title="Demander un report">
                                        <CalendarClock className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                    {ss > now && (
                                      <button onClick={() => setCoachCancelDialog(b)}
                                        data-testid={`coach-cancel-table-${b.booking_id}`}
                                        className="p-1.5 rounded text-red-400 hover:bg-red-500/10" title="Annuler (remboursement client)"><X className="w-3.5 h-3.5" /></button>
                                    )}
                                  </>
                                );
                              })()}
                              {b.status === 'completed' && b.ai_report && (
                                <button data-testid={`coach-download-pdf-${b.booking_id}`}
                                  onClick={async () => {
                                    try {
                                      const res = await fetch(`${API}/marketplace/sessions/${b.booking_id}/report/pdf`, { headers: { Authorization: `Bearer ${token}` } });
                                      if (!res.ok) throw new Error('Erreur');
                                      const blob = await res.blob();
                                      const url = URL.createObjectURL(blob);
                                      const a = document.createElement('a'); a.href = url; a.download = `rapport_${b.date}.pdf`;
                                      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
                                    } catch (err) { toast.error('Erreur PDF'); }
                                  }}
                                  className="p-1.5 rounded bg-[#D4AF37]/15 text-[#92710A] hover:bg-[#D4AF37]/25" title="PDF">
                                  <Download className="w-3.5 h-3.5" />
                                </button>
                              )}
                              {['confirmed', 'completed'].includes(b.status) && (
                                <button
                                  onClick={() => toggleSharedCtx(b.booking_id)}
                                  data-testid={`coach-shared-ctx-toggle-table-${b.booking_id}`}
                                  className="p-1.5 rounded hover:bg-[#D4AF37]/20"
                                  title="Contexte Owen partagé"
                                  style={{ color: '#B7901F' }}
                                >
                                  🔒
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {openedSharedCtx[b.booking_id] && (
                          <tr data-testid={`coach-shared-ctx-row-${b.booking_id}`}>
                            <td colSpan={7} className="px-4 py-3 bg-[#FAFAF8]">
                              <CoachBookingSharedContext bookingId={b.booking_id} />
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </TabsContent>

          {/* Availability tab — Visual Calendar */}
          <TabsContent value="availability">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 pb-20 lg:pb-0">
              {/* Calendar */}
              <div className="lg:col-span-2 bg-white border border-[#E8E5DE] rounded-xl shadow-sm p-4 sm:p-6" data-testid="availability-calendar">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-[#111827] font-medium">Disponibilités</h3>
                  <button onClick={() => setRangeMode(!rangeMode)} data-testid="toggle-range-mode"
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-all ${rangeMode ? 'bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#111827] shadow-[0_2px_8px_rgba(180,150,40,0.25)] font-medium' : 'bg-white/5 text-[#4B5563] hover:text-[#111827]'}`}>
                    <CalendarRange className="w-3.5 h-3.5" /> Plage de dates
                  </button>
                </div>

                {/* Range mode panel */}
                {rangeMode && (
                  <div className="bg-white/5 rounded-xl p-4 mb-4 space-y-3" data-testid="range-mode-panel">
                    <p className="text-[#4B5563] text-xs">Appliquer les mêmes créneaux sur une période.</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-[#4B5563] block mb-1">Date début</label>
                        <input type="date" value={rangeStart || ''} onChange={(e) => setRangeStart(e.target.value)}
                          className="w-full px-3 py-2 bg-[#F8F7F4] border border-[#DDD9D0] rounded-lg text-[#111827] text-sm" />
                      </div>
                      <div>
                        <label className="text-xs text-[#4B5563] block mb-1">Date fin</label>
                        <input type="date" value={rangeEnd || ''} onChange={(e) => setRangeEnd(e.target.value)}
                          className="w-full px-3 py-2 bg-[#F8F7F4] border border-[#DDD9D0] rounded-lg text-[#111827] text-sm" />
                      </div>
                    </div>
                    {rangeSlots.map((rs, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <TimePickerWheel value={rs.start_time} label="range-start"
                          onChange={(val) => { const u = [...rangeSlots]; u[i] = { ...u[i], start_time: val }; setRangeSlots(u); }} />
                        <span className="text-[#4B5563]">—</span>
                        <TimePickerWheel value={rs.end_time} label="range-end"
                          onChange={(val) => { const u = [...rangeSlots]; u[i] = { ...u[i], end_time: val }; setRangeSlots(u); }} />
                        {rangeSlots.length > 1 && (
                          <button onClick={() => setRangeSlots(rangeSlots.filter((_, j) => j !== i))}
                            className="p-1.5 text-[#4B5563] hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                        )}
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <button onClick={() => setRangeSlots([...rangeSlots, { start_time: '09:00', end_time: '17:00' }])}
                        className="flex-1 py-2 bg-white/5 rounded-lg text-[#4B5563] text-xs hover:bg-white/10 flex items-center justify-center gap-1">
                        <Plus className="w-3.5 h-3.5" /> Créneau
                      </button>
                      <button onClick={applyDateRange} disabled={!rangeStart || !rangeEnd} data-testid="apply-range"
                        className="flex-1 py-2 bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#111827] shadow-[0_2px_8px_rgba(180,150,40,0.25)] rounded-lg text-xs font-medium hover:bg-[#F3D060] disabled:opacity-40 transition-all">
                        Appliquer
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <button onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))}
                      className="p-2 rounded-lg bg-white/5 text-[#4B5563] hover:text-[#111827] hover:bg-white/10 transition-all">
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-[#111827] text-sm font-medium w-36 text-center">
                      {MONTH_NAMES[calendarMonth.getMonth()]} {calendarMonth.getFullYear()}
                    </span>
                    <button onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))}
                      className="p-2 rounded-lg bg-white/5 text-[#4B5563] hover:text-[#111827] hover:bg-white/10 transition-all">
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Day names */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'].map(d => (
                    <div key={d} className="text-center text-xs text-[#4B5563] py-1">{d}</div>
                  ))}
                </div>

                {/* Calendar grid */}
                <div className="grid grid-cols-7 gap-1">
                  {calendarDays().map((date, i) => {
                    if (!date) return <div key={`empty-${i}`} />;
                    const key = toLocalDateStr(date);
                    const available = isDateAvailable(date);
                    const today = new Date(); today.setHours(0,0,0,0);
                    const isPast = date < today;
                    const isSelected = selectedCalDate && key === toLocalDateStr(selectedCalDate);
                    // Check if any bookings on this date
                    const hasBooking = bookings.some(b => b.date === key && b.status === 'confirmed');

                    return (
                      <button key={key}
                        data-testid={`cal-day-${key}`}
                        onClick={() => !isPast && selectDate(date)}
                        disabled={isPast}
                        className={`
                          aspect-square rounded-lg text-sm font-medium transition-all flex items-center justify-center relative
                          ${isPast ? 'text-zinc-700 cursor-not-allowed' :
                            isSelected ? 'ring-2 ring-[#D4AF37] bg-[#D4AF37]/20 text-[#111827]' :
                            available ? 'bg-green-500/15 text-green-700 hover:bg-green-500/25' :
                            'bg-white/5 text-[#4B5563] hover:bg-white/10 hover:text-[#4B5563]'}
                        `}>
                        {date.getDate()}
                        {hasBooking && !isPast && (
                          <span className="absolute bottom-1 w-1 h-1 rounded-full bg-[#D4AF37]" />
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Legend */}
                <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-[#E8E5DE]">
                  <span className="flex items-center gap-1.5 text-xs text-[#4B5563]">
                    <span className="w-3 h-3 rounded bg-green-500/15 border border-green-500/30" /> Disponible
                  </span>
                  <span className="flex items-center gap-1.5 text-xs text-[#4B5563]">
                    <span className="w-3 h-3 rounded bg-white/5 border border-[#DDD9D0]" /> Indisponible
                  </span>
                  <span className="flex items-center gap-1.5 text-xs text-[#4B5563]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" /> Réservation
                  </span>
                </div>
                <p className="text-[#4B5563] text-xs mt-3">Cliquez sur un jour pour gérer sa disponibilité.</p>
              </div>

              {/* Day detail panel */}
              <div className="bg-white border border-[#E8E5DE] rounded-xl shadow-sm p-4 sm:p-6" data-testid="day-detail-panel">
                {selectedCalDate ? (
                  <>
                    <h3 className="text-[#111827] font-medium mb-1">
                      {DAYS[selectedDow]} {selectedCalDate.getDate()} {MONTH_NAMES[selectedCalDate.getMonth()]}
                    </h3>

                    {isDateAvailable(selectedCalDate) ? (
                      <>
                        <div className="flex items-center justify-between mb-4">
                          <p className="text-green-700 text-xs font-medium flex items-center gap-1.5">
                            <Check className="w-3.5 h-3.5" /> Disponible — {selectedDaySlots.length} créneau(x)
                          </p>
                          <button onClick={disableDate} data-testid="disable-date-btn"
                            className="text-xs text-red-500 hover:text-red-700 font-medium transition-colors">
                            Rendre indisponible
                          </button>
                        </div>

                        <div className="space-y-3 mb-4">
                          {selectedDaySlots.map((slot, idx) => (
                            <div key={idx} className="bg-[#F8F7F4] rounded-xl p-3">
                              <div className="flex items-center gap-3">
                                <div className="flex-1">
                                  <label className="text-xs text-[#4B5563] block mb-1">Début</label>
                                  <TimePickerWheel value={slot.start_time} label={`start-${idx}`}
                                    onChange={(val) => updateSelectedDateSlot(idx, 'start_time', val)} />
                                </div>
                                <div className="flex-1">
                                  <label className="text-xs text-[#4B5563] block mb-1">Fin</label>
                                  <TimePickerWheel value={slot.end_time} label={`end-${idx}`}
                                    onChange={(val) => updateSelectedDateSlot(idx, 'end_time', val)} />
                                </div>
                                <button onClick={() => removeSelectedDateSlot(idx)}
                                  className="mt-5 p-2 text-[#4B5563] hover:text-red-400 transition-colors">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>

                        <button onClick={addSelectedDateSlot}
                          className="w-full py-2 mb-4 bg-[#F8F7F4] border border-dashed border-[#DDD9D0] rounded-lg text-[#4B5563] text-sm hover:border-[#D4AF37] hover:text-[#111827] transition-all flex items-center justify-center gap-1.5">
                          <Plus className="w-4 h-4" /> Ajouter un créneau
                        </button>
                      </>
                    ) : (
                      <div className="text-center py-6">
                        <p className="text-[#4B5563] text-sm mb-4">Ce jour est indisponible.</p>
                        <button onClick={enableDate} data-testid="enable-date-btn"
                          className="px-6 py-2.5 bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#111827] shadow-[0_2px_8px_rgba(180,150,40,0.25)] rounded-full font-medium text-sm hover:brightness-110 transition-all">
                          Rendre disponible
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Calendar className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
                    <p className="text-[#4B5563] text-sm">Sélectionnez un jour dans le calendrier</p>
                  </div>
                )}

                {/* Save button — visible inline on desktop */}
                <button data-testid="save-availability-button" onClick={handleSaveSlots} disabled={savingSlots}
                  className="hidden lg:block w-full py-3 bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#111827] shadow-[0_2px_8px_rgba(180,150,40,0.25)] rounded-full font-medium text-sm hover:bg-[#F3D060] disabled:opacity-50 transition-all">
                  {savingSlots ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </div>

            {/* Save button — sticky bottom on mobile for accessibility */}
            <div className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#F8F7F4]/95 backdrop-blur-sm border-t border-[#DDD9D0] p-4 safe-area-bottom">
              <button data-testid="save-availability-button-mobile" onClick={handleSaveSlots} disabled={savingSlots}
                className="w-full py-3.5 bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#111827] shadow-[0_2px_8px_rgba(180,150,40,0.25)] rounded-full font-semibold text-sm hover:bg-[#F3D060] disabled:opacity-50 transition-all shadow-lg shadow-[#D4AF37]/20">
                {savingSlots ? 'Enregistrement...' : 'Enregistrer les disponibilités'}
              </button>
            </div>
          </TabsContent>

          {/* Enterprise Offers tab — Coach receives & responds to B2B shortlist offers (Vague 4 P0) */}
          <TabsContent value="enterprise-offers">
            <CoachEnterpriseOffers token={token} />
          </TabsContent>

          {/* Payouts tab — Justificatifs de paiement coach */}
          <TabsContent value="payouts">
            <CoachPayoutsTab />
          </TabsContent>

          {/* Settings tab — Profile editing */}
          <TabsContent value="settings">
            <ProfileEditor profile={profile} token={token} onSaved={fetchProfile} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Coach: reschedule request dialog (wraps BookingRescheduleDialog + message) */}
      {coachRescheduleBooking && (
        <BookingRescheduleDialog
          open={!!coachRescheduleBooking}
          onOpenChange={(o) => { if (!o) { setCoachRescheduleBooking(null); setCoachRescheduleMessage(''); } }}
          booking={coachRescheduleBooking}
          token={token}
          onConfirm={handleCoachRescheduleRequest}
          title="Proposer un nouveau créneau au client"
          confirmLabel="Envoyer la demande"
        />
      )}

      {/* Coach: cancel confirmation dialog with refund warning */}
      <Dialog open={!!coachCancelDialog} onOpenChange={(o) => !o && setCoachCancelDialog(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="coach-cancel-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1C1147]">
              <AlertTriangle className="w-5 h-5 text-red-600" /> Annuler la séance ?
            </DialogTitle>
          </DialogHeader>
          {coachCancelDialog && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-[#4B3869]">
                Séance avec <strong>{coachCancelDialog.client_name || coachCancelDialog.client_email}</strong><br />
                {formatDateLongFR(coachCancelDialog.date)} à {coachCancelDialog.start_time}
              </p>
              <div className="rounded-xl p-4 border bg-amber-50 border-amber-200">
                <p className="text-sm font-bold text-amber-800 flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4" /> Remboursement intégral automatique
                </p>
                <p className="text-xs text-amber-800/90 mt-1">
                  L'utilisateur sera remboursé(e) automatiquement de <strong>{coachCancelDialog.amount}€</strong> sur son moyen de paiement d'origine. Il recevra également un email de confirmation.
                </p>
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setCoachCancelDialog(null)}
                  className="px-4 py-2 text-sm font-semibold text-[#4B3869] rounded-full hover:bg-gray-50">
                  Revenir
                </button>
                <button
                  data-testid="coach-confirm-cancel-btn"
                  onClick={() => { updateBookingStatus(coachCancelDialog.booking_id, 'cancelled'); setCoachCancelDialog(null); }}
                  className="px-4 py-2 text-sm font-semibold bg-red-600 text-white rounded-full hover:bg-red-700">
                  Confirmer l'annulation
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Change Plan Dialog */}
      <Dialog open={showChangePlan} onOpenChange={(o) => !o && setShowChangePlan(false)}>
        <DialogContent className="bg-white border border-[#E8E5DE] text-[#111827] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-[#111827] text-lg font-display">Changer de plan</DialogTitle>
          </DialogHeader>
          <p className="text-[#4B5563] text-sm mb-4 font-body">
            <span className="font-medium text-[#111827]">Upgrade</span> : prorata facturé aujourd'hui via Stripe. <span className="font-medium text-[#111827]">Downgrade</span> : appliqué au prochain renouvellement (gratuit).
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {availablePlans.map(plan => {
              const style = PLAN_TIER_STYLES[plan.plan_id];
              if (!style) return null;
              const { Icon, accent } = style;
              const isCurrent = profile?.subscription_plan === plan.plan_id;
              const preview = planPreviews[plan.plan_id];
              const isUpgrade = preview?.type === 'upgrade';
              const isDowngrade = preview?.type === 'downgrade';
              return (
                <button
                  key={plan.plan_id}
                  data-testid={`change-to-plan-${plan.plan_id}`}
                  onClick={() => !isCurrent && handleChangePlan(plan.plan_id)}
                  disabled={subActionLoading || isCurrent}
                  className={`relative p-4 rounded-xl border text-left transition-all ${
                    isCurrent
                      ? 'border-[#D4AF37] bg-[#FEF9E7] cursor-default'
                      : 'border-[#DDD9D0] bg-white hover:border-[#D4AF37]/60 hover:shadow-md disabled:opacity-50'
                  }`}
                >
                  {isCurrent && (
                    <div className="absolute top-2 right-2 px-2 py-0.5 bg-[#D4AF37] text-[#111827] text-[9px] font-semibold rounded-full">
                      ACTUEL
                    </div>
                  )}
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-4 h-4" style={{ color: accent }} />
                    <p className="text-[#111827] font-medium text-sm">{plan.label}</p>
                  </div>
                  <p className="text-[#111827] text-2xl font-light leading-tight">{plan.price_eur}€<span className="text-xs text-[#4B5563] font-normal">/mois</span></p>
                  <p className="text-[#4B5563] text-[11px] mt-1">+ {plan.commission_rate}% de commission</p>

                  {/* Live preview */}
                  {!isCurrent && preview && (
                    <div className={`mt-3 pt-3 border-t ${isUpgrade ? 'border-[#D4AF37]/40' : 'border-zinc-200'}`}>
                      {isUpgrade && (
                        <>
                          <p className="text-[10px] uppercase tracking-wider text-[#92710A] font-semibold">Upgrade</p>
                          <p className="text-[#111827] text-sm font-medium mt-0.5" data-testid={`prorata-amount-${plan.plan_id}`}>
                            {preview.prorata_amount?.toFixed(2)}€ aujourd'hui
                          </p>
                          <p className="text-[10px] text-[#4B5563] mt-0.5">Prorata · {preview.days_remaining}j restants</p>
                        </>
                      )}
                      {isDowngrade && (
                        <>
                          <p className="text-[10px] uppercase tracking-wider text-[#4B5563] font-semibold">Downgrade</p>
                          <p className="text-[#111827] text-sm font-medium mt-0.5">Gratuit</p>
                          <p className="text-[10px] text-[#4B5563] mt-0.5">
                            Appliqué le {preview.effective_date ? new Date(preview.effective_date).toLocaleDateString('fr-FR') : '—'}
                          </p>
                        </>
                      )}
                    </div>
                  )}

                  {/* Upsell insight — dynamic break-even badge (upgrade only, commission lower) */}
                  {!isCurrent && isUpgrade && (() => {
                    const insight = upgradeInsights?.opportunities?.find(o => o.target_plan === plan.plan_id);
                    if (!insight || insight.break_even_sessions == null) return null;
                    const recoStyles = {
                      recommended: { bg: 'bg-[#FEF9E7]', border: 'border-[#D4AF37]', text: 'text-[#92710A]', label: 'Recommandé' },
                      neutral: { bg: 'bg-[#F5F3EE]', border: 'border-[#DDD9D0]', text: 'text-[#4B5563]', label: 'À envisager' },
                      not_recommended: { bg: 'bg-white', border: 'border-[#E8E5DE]', text: 'text-[#4B5563]', label: 'Pas encore rentable' },
                    };
                    const style = recoStyles[insight.recommendation] || recoStyles.neutral;
                    return (
                      <div
                        data-testid={`upsell-insight-${plan.plan_id}`}
                        className={`mt-2 px-2 py-1.5 rounded-md border ${style.bg} ${style.border}`}
                      >
                        <p className={`text-[9px] uppercase tracking-wider font-semibold ${style.text}`}>
                          {style.label}
                        </p>
                        <p className="text-[11px] text-[#111827] leading-snug mt-0.5 font-body">
                          {insight.message}
                        </p>
                        {insight.projected_monthly_savings > 0 && (
                          <p className="text-[10px] text-[#92710A] font-medium mt-0.5">
                            +{insight.projected_monthly_savings.toFixed(2)}€/mois nets à votre rythme actuel
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </button>
              );
            })}
          </div>
          <div className="flex justify-end mt-4">
            <button onClick={() => setShowChangePlan(false)} className="px-4 py-2 text-xs rounded-full bg-zinc-100 text-[#111827] hover:bg-zinc-200">
              Fermer
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Dialog */}
      <Dialog open={showCancelConfirm} onOpenChange={(o) => !o && setShowCancelConfirm(false)}>
        <DialogContent className="bg-white border border-red-200 text-[#111827] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 text-lg font-display flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Résilier mon abonnement ?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-[#374151] text-sm">
              Vous êtes sur le plan <strong>{PLAN_TIER_STYLES[profile?.subscription_plan]?.label || '—'}</strong>.
            </p>
            <ul className="space-y-1.5 text-sm text-[#4B5563]">
              <li className="flex items-start gap-2">
                <X className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                Votre profil ne sera plus visible sur la marketplace.
              </li>
              <li className="flex items-start gap-2">
                <X className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                Vous ne recevrez plus de nouvelles réservations.
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                Vos sessions déjà réservées restent honorées.
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                Vous pourrez réactiver votre abonnement à tout moment.
              </li>
            </ul>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <button
              onClick={() => setShowCancelConfirm(false)}
              className="px-4 py-2 text-xs rounded-full bg-zinc-100 text-[#111827] hover:bg-zinc-200"
              data-testid="cancel-cancel-subscription"
            >
              Annuler
            </button>
            <button
              onClick={handleCancelSubscription}
              disabled={subActionLoading}
              data-testid="confirm-cancel-subscription"
              className="px-4 py-2 text-xs font-semibold bg-red-600 text-white rounded-full hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
            >
              {subActionLoading && <Loader2 className="w-3 h-3 animate-spin" />}
              Confirmer la résiliation
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
